import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowDown } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { ReactComponent as KairosFullLogo } from '../imgs/kairos_white.svg';

const Hero: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section className="min-h-screen flex items-center justify-center px-6 pt-32 pb-20 relative overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Animated background gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)',
          filter: 'blur(80px)'
        }}
      />

      {/* Logo - Background on mobile, prominent on desktop */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="absolute md:relative md:mb-12 md:px-20 flex justify-center logo-container"
      >
        <div
          className="relative p-0 md:p-12 rounded-3xl glass-hero-logo mobile-logo-background md:mobile-logo-foreground"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05))',
            backdropFilter: 'blur(40px) saturate(200%)',
            WebkitBackdropFilter: 'blur(40px) saturate(200%)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTop: '3px solid rgba(255, 255, 255, 0.6)',
            borderLeft: '3px solid rgba(255, 255, 255, 0.6)',
            boxShadow: `
              0 8px 32px rgba(99, 102, 241, 0.5),
              0 20px 60px rgba(99, 102, 241, 0.4),
              0 40px 100px rgba(236, 72, 153, 0.3),
              inset 0 4px 30px rgba(255, 255, 255, 0.3),
              inset 0 -4px 30px rgba(99, 102, 241, 0.2)
            `
          }}
        >
          {/* Animated shine overlay */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden">
            <div
              className="absolute inset-0 shine-effect"
              style={{
                background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.4) 50%, transparent 70%)',
                transform: 'translateX(-100%) translateY(-100%) rotate(45deg)',
                animation: 'shine 4s ease-in-out infinite'
              }}
            />
          </div>

          {/* Glowing logo */}
          <KairosFullLogo
            className="w-80 h-80 sm:w-96 sm:h-96 md:w-64 md:h-64 relative z-10 logo-glow"
            style={{
              fill: 'url(#hero-logo-gradient)',
              filter: 'drop-shadow(0 0 40px rgba(99, 102, 241, 1)) drop-shadow(0 0 80px rgba(236, 72, 153, 0.6))'
            }}
          />

          {/* Gradient definition */}
          <svg width="0" height="0">
            <defs>
              <linearGradient id="hero-logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: 'var(--color-primary)', stopOpacity: 1 }}>
                  <animate attributeName="stop-color" values="var(--color-primary);var(--color-accent);var(--color-primary)" dur="6s" repeatCount="indefinite" />
                </stop>
                <stop offset="50%" style={{ stopColor: 'var(--color-accent)', stopOpacity: 1 }}>
                  <animate attributeName="stop-color" values="var(--color-accent);var(--color-primary);var(--color-accent)" dur="6s" repeatCount="indefinite" />
                </stop>
                <stop offset="100%" style={{ stopColor: 'var(--color-primary)', stopOpacity: 1 }}>
                  <animate attributeName="stop-color" values="var(--color-primary);var(--color-accent);var(--color-primary)" dur="6s" repeatCount="indefinite" />
                </stop>
              </linearGradient>
            </defs>
          </svg>
        </div>
      </motion.div>

      <style>{`
        /* Logo container positioning */
        .logo-container {
          top: 45%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (min-width: 768px) {
          .logo-container {
            position: relative;
            top: auto;
            left: auto;
            transform: none;
          }
        }

        /* Mobile: Logo as faded background */
        .mobile-logo-background {
          opacity: 0.15;
          z-index: 1;
        }

        /* Desktop: Logo fully visible */
        @media (min-width: 768px) {
          .mobile-logo-background {
            opacity: 1;
            z-index: auto;
          }
        }

        .glass-hero-logo {
          animation: float-glow 6s ease-in-out infinite, glass-shimmer 8s ease-in-out infinite;
        }

        @keyframes float-glow {
          0%, 100% {
            transform: translateY(0px) scale(1);
            box-shadow:
              0 8px 32px rgba(99, 102, 241, 0.5),
              0 20px 60px rgba(99, 102, 241, 0.4),
              0 40px 100px rgba(236, 72, 153, 0.3),
              inset 0 4px 30px rgba(255, 255, 255, 0.3),
              inset 0 -4px 30px rgba(99, 102, 241, 0.2);
          }
          50% {
            transform: translateY(-10px) scale(1.02);
            box-shadow:
              0 10px 40px rgba(99, 102, 241, 0.8),
              0 25px 80px rgba(99, 102, 241, 0.6),
              0 50px 120px rgba(236, 72, 153, 0.5),
              inset 0 6px 40px rgba(255, 255, 255, 0.4),
              inset 0 -6px 40px rgba(99, 102, 241, 0.3);
          }
        }

        @keyframes glass-shimmer {
          0%, 100% {
            border-top-color: rgba(255, 255, 255, 0.6);
            border-left-color: rgba(255, 255, 255, 0.6);
          }
          50% {
            border-top-color: rgba(255, 255, 255, 0.9);
            border-left-color: rgba(255, 255, 255, 0.9);
          }
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(200%) translateY(200%) rotate(45deg);
          }
        }

        .logo-glow {
          animation: logo-pulse 4s ease-in-out infinite;
        }

        @keyframes logo-pulse {
          0%, 100% {
            filter: drop-shadow(0 0 40px rgba(99, 102, 241, 1)) drop-shadow(0 0 80px rgba(236, 72, 153, 0.6));
          }
          50% {
            filter: drop-shadow(0 0 60px rgba(99, 102, 241, 1)) drop-shadow(0 0 100px rgba(236, 72, 153, 0.8)) drop-shadow(0 0 120px rgba(99, 102, 241, 0.4));
          }
        }
      `}</style>

      <div className="max-w-5xl mx-auto text-center relative z-20">

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {t.hero.titleStart}{' '}
          <span
            style={{
              background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {t.hero.titleHighlight}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-lg md:text-2xl lg:text-3xl font-medium mb-12 max-w-4xl mx-auto"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <a
            href="#diagnose"
            className="glass-strong inline-block px-8 py-4 rounded-xl font-bold text-xl transition-all duration-300 hover:scale-105"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'var(--color-text-primary)',
              border: '2px solid var(--color-primary)',
              boxShadow: '0 4px 16px rgba(99, 102, 241, 0.2)'
            }}
          >
            {t.hero.cta}
          </a>
          <a
            href="#get-started"
            className="glass-strong inline-block px-8 py-4 rounded-xl font-bold text-xl transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
              color: 'white',
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)'
            }}
          >
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-20"
        >
          {React.createElement(FaArrowDown, {
            className: "w-8 h-8 mx-auto animate-bounce",
            style: { color: 'var(--color-primary)' }
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
